/**
 * Calendar utility functions for determining day types and working days
 */

import { getDay } from 'date-fns'
import type { IntlShape } from 'react-intl'

/**
 * Determines if a day is a working day based on custom work week configuration
 *
 * @param dayOfWeek - Day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 * @param workWeekDays - Optional array of working day numbers. If not provided, defaults to Monday-Friday (1-5)
 * @returns True if the day is a working day
 *
 * @example
 * isWorkingDay(1, [0, 2, 4]) // true (Monday is in custom work week)
 * isWorkingDay(1, undefined) // true (Monday is default working day)
 * isWorkingDay(0, undefined) // false (Sunday is not default working day)
 */
export function isWorkingDay(dayOfWeek: number, workWeekDays?: number[]): boolean {
  if (workWeekDays && workWeekDays.length > 0) {
    // Use custom work week days from API
    return workWeekDays.includes(dayOfWeek)
  }
  // Fallback: standard weekdays (Monday-Friday)
  return dayOfWeek >= 1 && dayOfWeek <= 5
}

/**
 * Determines the type of a calendar day based on PTO range, holidays, weekends, and working days
 *
 * Priority order:
 * 1. Holiday (highest priority - purple)
 * 2. Non-working day (weekend or configured non-working day - light gray)
 * 3. PTO day (working day in PTO range - green)
 * 4. Normal day (default)
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param date - Date object for the day
 * @param ptoDateSet - Set of PTO date strings
 * @param holidayDateSet - Set of holiday date strings
 * @param weekendDateSet - Set of weekend date strings
 * @param workWeekDays - Optional array of working day numbers
 * @param holidayMap - Map of holiday dates to names
 * @returns Object with day type and optional label
 */
export function getDayType(
  dateStr: string,
  date: Date,
  ptoDateSet: Set<string>,
  holidayDateSet: Set<string>,
  weekendDateSet: Set<string>,
  workWeekDays?: number[],
  holidayMap?: Map<string, string>,
  formatMessage?: IntlShape['formatMessage']
): { type: 'pto' | 'holiday' | 'non-working' | 'normal'; label?: string } {
  const dayOfWeek = getDay(date)
  const isWorking = isWorkingDay(dayOfWeek, workWeekDays)

  // Holidays take priority (purple) - mark as holiday regardless of PTO range
  if (holidayDateSet.has(dateStr)) {
    return {
      type: 'holiday',
      label: holidayMap?.get(dateStr) || (formatMessage ? formatMessage({ id: 'energyLevel.calendar.holiday' }) : 'Holiday'),
    }
  }

  // Non-working days take second priority (light gray)
  // Mark if in weekendsIncluded or is not a working day
  if (weekendDateSet.has(dateStr)) {
    return {
      type: 'non-working',
      label: formatMessage ? formatMessage({ id: 'energyLevel.calendar.nonWorkingDay' }) : 'Non-working day',
    }
  }

  if (!isWorking) {
    return {
      type: 'non-working',
      label: formatMessage ? formatMessage({ id: 'energyLevel.calendar.nonWorkingDay' }) : 'Non-working day',
    }
  }

  // PTO days (working days in the PTO range that are not holidays)
  // Check if date is in ptoDateSet AND it's not a holiday AND is a working day
  if (ptoDateSet.has(dateStr) && !holidayDateSet.has(dateStr) && isWorking) {
    return {
      type: 'pto',
      label: formatMessage ? formatMessage({ id: 'energyLevel.calendar.pto' }) : 'PTO',
    }
  }

  // For all other days in the week range, show as normal
  return {
    type: 'normal',
  }
}
